import { useState, useEffect, FormEvent } from 'react';
import { getProducts, createProduct, updateProduct, deleteProduct, Product, getStripeConnectStatus, createStripeConnect, StripeConnectStatus, getShopLink, updateShopLink, ShopLink } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import ImageUploader from '../components/ImageUploader';
import ShopNav from '../components/ShopNav';
import './Products.css';
import { overlayClose } from '../utils/overlayClose';

const CATEGORIES = ['Kits', 'Training Gear', 'Accessories', 'Merchandise'];
// Mirrors MAX_PERSONALISATION_LENGTH in lambda/routes/products.ts — the API
// clamps to it regardless, this just stops the input offering more.
const MAX_PERSONALISATION_LENGTH = 40;
// Mirrors MAX_DELIVERY_FEE in lambda/routes/products.ts.
const MAX_DELIVERY_FEE = 50;
const FREE_PRODUCT_LIMIT = 5;

const SIZE_GROUPS = [
  {
    label: 'Youth',
    sizes: ['YXS', 'YS', 'YM', 'YL', 'YXL'],
  },
  {
    label: 'Adult',
    sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
  },
  {
    label: 'Socks',
    sizes: ['Child 9-12', 'Junior 12.5-3.5', 'Junior 4-7', 'Adult 7-11'],
  },
];

/** "kitlocker.com" from a full address, for the product list. */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function Products() {
  const { plan, demoMode, refreshPlan } = useAuth();

  const [products, setProducts] = useState<Product[]>([]);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [stripeConnect, setStripeConnect] = useState<StripeConnectStatus | null>(null);
  const [connectLoading, setConnectLoading] = useState(true);
  // Sample text for the personalisation preview — never saved, just lets a club
  // type into the box their buyers will see.
  const [previewText, setPreviewText] = useState('SMITH 9');
  const [shopLink, setShopLink] = useState<ShopLink | null>(null);
  const [shopLinkInput, setShopLinkInput] = useState('');
  const [savingShopLink, setSavingShopLink] = useState(false);
  const [shopLinkMessage, setShopLinkMessage] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    salePrice: '',
    category: CATEGORIES[0] || '',
    sizes: [] as string[],
    stock: '',
    trackStock: true,
    personalisationEnabled: false,
    personalisationLabel: 'Name and number',
    personalisationMaxLength: '15',
    deliveryAvailable: false,
    deliveryFee: '3.95',
    images: [] as string[],
    sellMode: 'here' as 'here' | 'link',
    externalUrl: '',
  });

  useEffect(() => {
    loadProducts();
    loadConnectStatus();
    loadShopLink();
    refreshPlan();
  }, []);

  const loadProducts = async () => {
    try {
      setIsLoading(true);
      const data = await getProducts();
      setProducts(data.products);
      setHiddenCount(data.hiddenCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setIsLoading(false);
    }
  };

  const loadConnectStatus = async () => {
    try {
      const status = await getStripeConnectStatus();
      setStripeConnect(status);
    } catch (err) {
      console.error('Failed to load Stripe Connect status:', err);
    } finally {
      setConnectLoading(false);
    }
  };

  const loadShopLink = async () => {
    try {
      const link = await getShopLink();
      setShopLink(link);
      setShopLinkInput(link.shopUrl);
    } catch {
      /* the field stays disabled */
    }
  };

  const saveShopLink = async () => {
    setSavingShopLink(true);
    setShopLinkMessage('');
    try {
      const res = await updateShopLink(shopLinkInput.trim());
      setShopLink(prev => (prev ? { ...prev, shopUrl: res.shopUrl } : prev));
      setShopLinkInput(res.shopUrl);
      setShopLinkMessage(res.shopUrl ? 'Saved. Your website now links to your shop.' : 'Link removed.');
    } catch (err) {
      setShopLinkMessage(err instanceof Error ? err.message : 'Could not save the link');
    } finally {
      setSavingShopLink(false);
    }
  };

  const handleConnectStripe = async () => {
    try {
      const { url } = await createStripeConnect();
      window.location.href = url;
    } catch (err: any) {
      setError(err.message || 'Failed to start Stripe Connect setup.');
    }
  };

  const isConnected = demoMode || stripeConnect?.connected === true;
  /* `connected` only means the Stripe account exists — true from the moment a
     club clicks Connect. Selling needs charges_enabled. Gating "Add Product"
     on the former let a club fill a public shop it could not take a penny
     through. */
  const canCharge = demoMode || stripeConnect?.chargesEnabled === true;
  const isFree = plan === 'free';
  const isPaid = plan === 'club' || plan === 'pro';
  const atProductLimit = isFree && products.length >= FREE_PRODUCT_LIMIT;
  // Club and Pro can list products that link elsewhere without Stripe.
  const canAddProduct = (canCharge || isPaid) && !atProductLimit;

  const handleOpenModal = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        description: product.description,
        price: product.externalUrl && !product.price ? '' : product.price.toString(),
        salePrice: product.salePrice?.toString() || '',
        category: product.category,
        sizes: product.sizes,
        stock: product.stock === null ? '' : String(product.stock),
        trackStock: product.stock !== null,
        personalisationEnabled: !!product.personalisation?.enabled,
        personalisationLabel: product.personalisation?.label || 'Name and number',
        personalisationMaxLength: String(product.personalisation?.maxLength ?? 15),
        deliveryAvailable: !!product.delivery?.available,
        deliveryFee: String(product.delivery?.fee ?? 3.95),
        images: product.images,
        sellMode: product.externalUrl ? 'link' : 'here',
        externalUrl: product.externalUrl || '',
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        salePrice: '',
        category: CATEGORIES[0] || '',
        sizes: [],
        stock: '',
        trackStock: true,
        personalisationEnabled: false,
        personalisationLabel: 'Name and number',
        personalisationMaxLength: '15',
        deliveryAvailable: false,
        deliveryFee: '3.95',
        images: [],
        sellMode: canCharge || !isPaid ? 'here' : 'link',
        externalUrl: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setShowPreview(false);
    setEditingProduct(null);
    setError('');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsSaving(true);

    try {
      const linked = formData.sellMode === 'link';
      const productData = {
        name: formData.name,
        description: formData.description,
        price: formData.price === '' ? 0 : parseFloat(formData.price),
        salePrice: formData.salePrice ? parseFloat(formData.salePrice) : undefined,
        category: formData.category,
        sizes: linked ? [] : formData.sizes,
        stock: linked ? null : formData.trackStock ? (parseInt(formData.stock) || 0) : null,
        personalisation: !linked && formData.personalisationEnabled
          ? {
            enabled: true,
            label: formData.personalisationLabel,
            maxLength: parseInt(formData.personalisationMaxLength) || 15,
          }
          : null,
        delivery: !linked && formData.deliveryAvailable
          ? { available: true, fee: parseFloat(formData.deliveryFee) || 0 }
          : null,
        images: formData.images,
        externalUrl: linked ? formData.externalUrl.trim() : null,
      };

      if (editingProduct) {
        await updateProduct(editingProduct.id, productData);
        setSuccessMessage('Product updated successfully');
      } else {
        await createProduct(productData);
        setSuccessMessage('Product created successfully');
      }

      await loadProducts();
      handleCloseModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save product');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this product?')) return;

    try {
      await deleteProduct(id);
      setSuccessMessage('Product deleted successfully');
      await loadProducts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete product');
    }
  };

  const handleSizeToggle = (size: string) => {
    setFormData(prev => ({
      ...prev,
      sizes: prev.sizes.includes(size)
        ? prev.sizes.filter(s => s !== size)
        : [...prev.sizes, size],
    }));
  };

  const handleImageUpload = (url: string) => {
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, url],
    }));
  };

  const handleImageRemove = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  const byLink = formData.sellMode === 'link';

  if (isLoading || connectLoading) {
    return <div className="loading">Loading products...</div>;
  }

  return (
    <div className="products-page">
      <ShopNav />
      <div className="page-header">
        <div>
          <h1 className="page-title">Products</h1>
          <p className="page-subtitle">
            Manage shop products
            {isFree && ` (${products.length}/${FREE_PRODUCT_LIMIT} on Free plan)`}
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn btn-primary"
          disabled={!canAddProduct}
          title={canAddProduct ? '' : atProductLimit ? 'Upgrade to add more products' : !isConnected ? 'Connect Stripe first to add products' : 'Finish your Stripe setup before adding products'}
        >
          Add Product
        </button>
      </div>

      <div className={`shop-link-card ${isPaid ? '' : 'gated'}`}>
        <div className="shop-link-head">
          <strong>Selling somewhere else?</strong>
          {!isPaid && <span className="upgrade-pill">Club &amp; Pro</span>}
        </div>
        <p>
          If your kit is sold through a supplier's club shop, you don't need to list every size and
          stock count here. Paste your shop's address and <em>Shop</em> appears in your website's menu,
          opening it in a new tab. You can also add products below that link straight to the right page
          on your shop, so they still show on your site.
        </p>
        <div className="shop-link-row">
          <input
            type="url"
            className="form-input"
            placeholder="https://your-club-shop.com"
            value={shopLinkInput}
            onChange={e => setShopLinkInput(e.target.value)}
            disabled={!isPaid || savingShopLink || !shopLink}
            aria-label="Your club shop's web address"
          />
          {isPaid ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={saveShopLink}
              disabled={!shopLink || savingShopLink || shopLinkInput.trim() === (shopLink?.shopUrl || '')}
            >
              {savingShopLink ? 'Checking…' : !shopLinkInput.trim() && shopLink?.shopUrl ? 'Remove link' : 'Save link'}
            </button>
          ) : (
            <a href="/upgrade" className="btn btn-primary">Upgrade</a>
          )}
        </div>
        <small className="form-hint">
          We check every link before it's saved and won't accept adult or harmful websites.
        </small>
        {shopLinkMessage && <p className="shop-link-message">{shopLinkMessage}</p>}
      </div>

      {/* Stripe Connect banner */}
      {!isConnected && (
        <div className="connect-banner">
          <div className="connect-banner-content">
            <span className="connect-banner-icon">🏪</span>
            <div>
              <strong>Connect Stripe to manage your shop</strong>
              <p>{isPaid
                ? "You need Stripe to sell products here by card. Products that link to your own shop don't need it."
                : 'You need to connect a Stripe account before you can add products and accept payments.'}</p>
            </div>
          </div>
          <button className="btn btn-primary" onClick={handleConnectStripe}>
            Connect Stripe
          </button>
        </div>
      )}

      {/* Free plan limit banner */}
      {/* Connected, but Stripe will not let them charge yet. Without this the
          page looked entirely healthy while every checkout failed. */}
      {isConnected && !canCharge && (
        <div className="connect-banner">
          <div className="connect-banner-content">
            <span className="connect-banner-icon">🏪</span>
            <div>
              <strong>Your shop can't take payments yet</strong>
              <p>
                Stripe still needs a few details before your club can be paid. Until that's
                finished, anyone trying to buy will see an error — so it's worth doing before
                you add products or share your shop.
              </p>
            </div>
          </div>
          <button className="btn btn-primary" onClick={handleConnectStripe}>
            Finish Stripe setup
          </button>
        </div>
      )}

      {isFree && isConnected && atProductLimit && hiddenCount === 0 && (
        <div className="limit-banner">
          <div className="limit-banner-content">
            <span className="limit-banner-icon">📦</span>
            <div>
              <strong>Product limit reached</strong>
              <p>Free plan allows up to {FREE_PRODUCT_LIMIT} products. Upgrade to Club for unlimited products and a lower transaction fee.</p>
            </div>
          </div>
          <a href="/upgrade" className="btn btn-primary">Upgrade</a>
        </div>
      )}

      {/* Hidden-from-public banner (over-limit, eg after downgrade) */}
      {hiddenCount > 0 && (
        <div className="limit-banner">
          <div className="limit-banner-content">
            <span className="limit-banner-icon">👁️</span>
            <div>
              <strong>{hiddenCount} {hiddenCount === 1 ? 'product is' : 'products are'} hidden from your public shop</strong>
              <p>Your plan shows the latest {FREE_PRODUCT_LIMIT} products on the website. Older ones are kept here but won't appear to visitors. Upgrade to show all.</p>
            </div>
          </div>
          <a href="/upgrade" className="btn btn-primary">Upgrade</a>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <div className="products-grid">
        {products.map((product) => (
          <div key={product.id} className={`product-card ${product.hiddenFromPublic ? 'hidden-from-public' : ''}`}>
            <div className="product-image-container">
              {product.images.length > 0 ? (
                <img src={product.images[0]} alt={product.name} className="product-image" />
              ) : (
                <div className="product-image-placeholder">No Image</div>
              )}
              {product.salePrice && (
                <div className="sale-badge">Sale</div>
              )}
              {product.hiddenFromPublic && (
                <div className="hidden-badge" title="Not shown on your public shop — upgrade to show all">
                  👁️ Hidden from public
                </div>
              )}
            </div>

            <div className="product-info">
              <h3 className="product-name">{product.name}</h3>
              <p className="product-category">{product.category}</p>
              <p className="product-description">{product.description}</p>

              <div className="product-meta">
                <div className="product-price">
                  {product.salePrice ? (
                    <>
                      <span className="price-sale">{formatCurrency(product.salePrice)}</span>
                      <span className="price-original">{formatCurrency(product.price)}</span>
                    </>
                  ) : (
                    <span className="price-regular">{product.externalUrl && !product.price ? 'Price on their shop' : formatCurrency(product.price)}</span>
                  )}
                </div>
                <div className="product-stock">
                  {product.externalUrl
                    ? <em>Sold on {hostOf(product.externalUrl)}</em>
                    : product.stock === null ? <em>Made to order</em> : <>Stock: <strong>{product.stock}</strong></>}
                </div>
              </div>

              {!product.externalUrl && (
                <div className="product-sizes">
                  {product.sizes.join(', ')}
                </div>
              )}

              <div className="product-actions">
                <button
                  onClick={() => handleOpenModal(product)}
                  className="btn btn-secondary btn-small"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(product.id)}
                  className="btn btn-danger btn-small"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {products.length === 0 && (
        <div className="empty-state">
          <p>No products yet. Create your first product to get started.</p>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" {...overlayClose(handleCloseModal)}>
          <div className="modal-content product-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingProduct ? 'Edit Product' : 'Add Product'}
              </h2>
              <button onClick={handleCloseModal} className="modal-close">
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name" className="form-label">Product Name</label>
                <input
                  id="name"
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="description" className="form-label">Description</label>
                <textarea
                  id="description"
                  className="form-textarea"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={4}
                />
              </div>

              <div className="form-group">
                <label className="form-label">How do people buy this?</label>
                <div className="sell-mode">
                  <label className={`sell-mode-option ${!canCharge ? 'disabled' : ''}`}>
                    <input
                      type="radio"
                      name="sellMode"
                      checked={formData.sellMode === 'here'}
                      disabled={!canCharge}
                      onChange={() => setFormData({ ...formData, sellMode: 'here' })}
                    />
                    <span>
                      <strong>Here, by card</strong>
                      <small>Sizes, stock and checkout on your site.{!canCharge && ' Needs Stripe set up first.'}</small>
                    </span>
                  </label>
                  <label className={`sell-mode-option ${!isPaid ? 'disabled' : ''}`}>
                    <input
                      type="radio"
                      name="sellMode"
                      checked={formData.sellMode === 'link'}
                      disabled={!isPaid}
                      onChange={() => setFormData({ ...formData, sellMode: 'link' })}
                    />
                    <span>
                      <strong>On another website</strong>
                      <small>The button opens this item on your club shop.{!isPaid && ' Club and Pro plans.'}</small>
                    </span>
                  </label>
                </div>
                {byLink && (
                  <>
                    <input
                      type="url"
                      className="form-input"
                      placeholder="https://your-club-shop.com/home-shirt"
                      value={formData.externalUrl}
                      onChange={(e) => setFormData({ ...formData, externalUrl: e.target.value })}
                      required
                      aria-label="Web address of this item on your club shop"
                    />
                    <small className="form-hint">
                      Paste the page for this item on your club shop. No sizes or stock needed here.
                      We check every link and won't accept adult or harmful websites.
                    </small>
                  </>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="price" className="form-label">{byLink ? 'Price (optional)' : 'Price (GBP)'}</label>
                  <input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-input"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required={!byLink}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="salePrice" className="form-label">Sale Price (Optional)</label>
                  <input
                    id="salePrice"
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-input"
                    value={formData.salePrice}
                    onChange={(e) => setFormData({ ...formData, salePrice: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="category" className="form-label">Category</label>
                  <select
                    id="category"
                    className="form-select"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className={`form-group ${byLink ? 'is-hidden' : ''}`}>
                  <label className="form-label">Stock</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem', marginBottom: 6 }}>
                    <input
                      type="checkbox"
                      checked={formData.trackStock}
                      onChange={(e) => setFormData({ ...formData, trackStock: e.target.checked })}
                      style={{ width: 'auto', margin: 0 }}
                    />
                    Track stock for this product
                  </label>
                  {formData.trackStock ? (
                    <input
                      id="stock"
                      type="number"
                      min="0"
                      className="form-input"
                      value={formData.stock}
                      onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                      required={!byLink}
                      placeholder="How many do you have?"
                    />
                  ) : (
                    <small className="form-hint">
                      Made to order — no stock count. It never shows as sold out, and orders
                      are yours to forward to your supplier.
                    </small>
                  )}
                </div>
              </div>

              <div className={`form-group ${byLink ? 'is-hidden' : ''}`}>
                <label className="form-label">Personalisation</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem', marginBottom: 6 }}>
                  <input
                    type="checkbox"
                    checked={formData.personalisationEnabled}
                    onChange={(e) => setFormData({ ...formData, personalisationEnabled: e.target.checked })}
                    style={{ width: 'auto', margin: 0 }}
                  />
                  Let buyers add their own text (name, number…)
                </label>
                {formData.personalisationEnabled ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 12, alignItems: 'start' }}>
                    <div>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.personalisationLabel}
                        onChange={(e) => setFormData({ ...formData, personalisationLabel: e.target.value })}
                        placeholder="Name and number"
                        maxLength={80}
                      />
                      <small className="form-hint">What to ask for — buyers see this above the box.</small>
                    </div>
                    <div>
                      <input
                        type="number"
                        min="1"
                        max={MAX_PERSONALISATION_LENGTH}
                        className="form-input"
                        value={formData.personalisationMaxLength}
                        onChange={(e) => setFormData({ ...formData, personalisationMaxLength: e.target.value })}
                      />
                      <small className="form-hint">Max characters ({MAX_PERSONALISATION_LENGTH} max).</small>
                    </div>
                    {/* What the parent actually sees. The label doubles as the
                        heading above their box, so a club can check its wording
                        reads right before saving. */}
                    <div className="personalisation-preview">
                      <span className="personalisation-preview-tag">Buyer sees</span>
                      <label className="personalisation-preview-label">
                        {formData.personalisationLabel || 'Personalisation'}
                      </label>
                      <input
                        type="text"
                        className="personalisation-preview-input"
                        value={previewText.slice(0, parseInt(formData.personalisationMaxLength) || 15)}
                        onChange={(e) => setPreviewText(e.target.value)}
                        maxLength={parseInt(formData.personalisationMaxLength) || 15}
                        placeholder="e.g. SMITH 9"
                      />
                      <small>
                        {previewText.slice(0, parseInt(formData.personalisationMaxLength) || 15).length}
                        /{parseInt(formData.personalisationMaxLength) || 15} characters — printed
                        exactly as you type it, so check the spelling.
                      </small>
                    </div>
                  </div>
                ) : (
                  <small className="form-hint">
                    Off — buyers order this exactly as listed. Turn it on for shirts and
                    training tops that get a name or squad number printed on them.
                  </small>
                )}
              </div>

              <div className={`form-group ${byLink ? 'is-hidden' : ''}`}>
                <label className="form-label">Delivery</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem', marginBottom: 6 }}>
                  <input
                    type="checkbox"
                    checked={formData.deliveryAvailable}
                    onChange={(e) => setFormData({ ...formData, deliveryAvailable: e.target.checked })}
                    style={{ width: 'auto', margin: 0 }}
                  />
                  Offer to post this item
                </label>
                {formData.deliveryAvailable ? (
                  <div style={{ maxWidth: 200 }}>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#888' }}>£</span>
                      <input
                        type="number"
                        min="0"
                        max={MAX_DELIVERY_FEE}
                        step="0.01"
                        className="form-input"
                        style={{ paddingLeft: 22 }}
                        value={formData.deliveryFee}
                        onChange={(e) => setFormData({ ...formData, deliveryFee: e.target.value })}
                      />
                    </div>
                    <small className="form-hint">
                      What it costs to post <em>this item on its own</em>. An order of several
                      items is one parcel, so buyers are charged the highest fee in their
                      basket — never the total, and never per item.
                    </small>
                  </div>
                ) : (
                  <small className="form-hint">
                    Collection only — buyers pick this up at training or on a match day.
                    If anything in a basket is collection-only, the whole order is.
                  </small>
                )}
              </div>

              <div className={`form-group ${byLink ? 'is-hidden' : ''}`}>
                <label className="form-label">Available Sizes</label>
                <div className="sizes-selector">
                  {SIZE_GROUPS.map(group => (
                    <div key={group.label} className="size-group">
                      <span className="size-group-label">{group.label}</span>
                      <div className="size-group-options">
                        {group.sizes.map(size => (
                          <label key={size} className="size-checkbox">
                            <input
                              type="checkbox"
                              checked={formData.sizes.includes(size)}
                              onChange={() => handleSizeToggle(size)}
                            />
                            <span className="size-label">{size}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Product Images</label>
                <div className="images-list">
                  {formData.images.map((image, index) => (
                    <div key={index} className="image-item">
                      <img src={image} alt={`Product ${index + 1}`} className="image-thumb" />
                      <button
                        type="button"
                        onClick={() => handleImageRemove(index)}
                        className="image-remove"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
                <ImageUploader onUpload={handleImageUpload} label="Add Image" />
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                {/* Preview reads the live form, not the saved product, so a club
                    can check an edit before committing it. */}
                <button
                  type="button"
                  onClick={() => setShowPreview(true)}
                  className="btn btn-outline"
                  disabled={!formData.name}
                  title={formData.name ? 'See how this looks to buyers' : 'Give the product a name first'}
                >
                  See preview
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : editingProduct ? 'Update Product' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Product-page preview — a mock of the storefront page built from the
          UNSAVED form, so a club sees the edit they are about to make rather
          than the version already live. Stacked above the edit modal; closing
          it returns them to the form with everything still filled in. */}
      {showPreview && (
        <div className="modal-overlay preview-overlay" {...overlayClose(() => setShowPreview(false))}>
          <div className="modal-content preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Preview</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowPreview(false)}
                aria-label="Close preview"
              >
                &times;
              </button>
            </div>

            <p className="preview-note">
              How this product looks to a parent on your shop. Nothing here is saved
              — close it and hit {editingProduct ? '"Update Product"' : '"Create Product"'} when you're happy.
            </p>

            <div className="preview-page">
              <div className="preview-page-image">
                {formData.images[0]
                  ? <img src={formData.images[0]} alt={formData.name} />
                  : <div className="preview-page-noimage">No image yet</div>}
              </div>

              <div className="preview-page-info">
                <span className="preview-page-category">{formData.category}</span>
                <h3 className="preview-page-name">{formData.name || 'Untitled product'}</h3>

                <p className="preview-page-price">
                  {formData.salePrice && parseFloat(formData.salePrice) > 0 ? (
                    <>
                      <span className="preview-page-was">{formatCurrency(parseFloat(formData.price) || 0)}</span>
                      {' '}{formatCurrency(parseFloat(formData.salePrice))}
                    </>
                  ) : formatCurrency(parseFloat(formData.price) || 0)}
                </p>

                {formData.description && (
                  <p className="preview-page-description">{formData.description}</p>
                )}

                {!byLink && formData.sizes.length > 0 && (
                  <div className="preview-page-sizes">
                    <strong>Select Size</strong>
                    <div>
                      {formData.sizes.map(s => (
                        <span key={s} className="preview-page-size">{s}</span>
                      ))}
                    </div>
                  </div>
                )}

                {!byLink && formData.personalisationEnabled && (
                  <div className="personalisation-preview" style={{ marginTop: 16 }}>
                    <label className="personalisation-preview-label">
                      {formData.personalisationLabel || 'Personalisation'}
                    </label>
                    <input
                      type="text"
                      className="personalisation-preview-input"
                      value={previewText.slice(0, parseInt(formData.personalisationMaxLength) || 15)}
                      onChange={(e) => setPreviewText(e.target.value)}
                      maxLength={parseInt(formData.personalisationMaxLength) || 15}
                      placeholder="e.g. SMITH 9"
                    />
                    <small>
                      {previewText.slice(0, parseInt(formData.personalisationMaxLength) || 15).length}
                      /{parseInt(formData.personalisationMaxLength) || 15} characters — printed
                      exactly as you type it, so check the spelling.
                    </small>
                  </div>
                )}

                {!byLink && (
                  <>
                  <p className="preview-page-delivery">
                    {formData.deliveryAvailable
                      ? <>Delivery from {formatCurrency(parseFloat(formData.deliveryFee) || 0)}, or collect at training</>
                      : <>Collection only — pick up at training or on a match day</>}
                  </p>

                  <p className="preview-page-stock">
                    {!formData.trackStock
                      ? <span className="in-stock">Made to order</span>
                      : (parseInt(formData.stock) || 0) > 10
                        ? <span className="in-stock">In Stock</span>
                        : (parseInt(formData.stock) || 0) > 0
                          ? <span className="low-stock">Only {parseInt(formData.stock)} left in stock</span>
                          : <span className="out-of-stock">Out of Stock</span>}
                  </p>
                  </>
                )}

                <button type="button" className="btn btn-primary preview-page-cta" disabled>
                  {byLink ? 'Buy on the club shop ↗' : 'Add to Cart'}
                </button>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-primary" onClick={() => setShowPreview(false)}>
                Back to editing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Products;
